/**
 * Plan 08 G4: bridge dressing. Pure geometry as a function of the segment index (hashValue, no RNG),
 * returned as parts the caller merges into the segment's two existing meshes (draw count unchanged).
 * Concrete parts carry a vertex-colour tint (PBR multiplies albedo by it) so a burnt car or a lamp
 * reads dark on the one concrete material. Everything sits on the outer shoulder beyond the rails,
 * so no prop can read as an obstacle inside the play lanes (|x| <= BRIDGE_HALF_WIDTH).
 *
 * Plan 20 P20.1 (lane A): `buildDressing(scene, index, opts?)` adds the per-level dressing
 * vocabulary — bundled, size-varied prop clusters (big + small + tiny) keyed by ring slot, a
 * tiny-details pass, and the dusk brazier bases. Partial-opts semantics are PINNED: a missing tier
 * behaves as 'high', a missing theme builds the legacy vocabulary only — so a NO-OPTS call renders
 * byte-identical to today's output (dressing.golden.json + the parity test in dressing.test.ts).
 * Theme parts merge into the same concrete mesh like everything else: vertex tints on the one
 * material, 0 added draw calls, 0 loaded assets (the plan-20 design rules).
 */
import { Color3, MeshBuilder, VertexBuffer } from '@babylonjs/core/pure';
import type { Mesh, Scene } from '@babylonjs/core/pure';
import { hashValue } from './scene';
import type { EnvTheme } from './scene';
import type { QualityTier } from '../gfx/quality';

export const LANE_HALF = 4.5; // = BRIDGE_HALF_WIDTH (world.ts re-exports the pair; kept literal to avoid a cycle)
const SEG = 40;
/** Walkway outside the rails: x ∈ [SHOULDER_IN, SHOULDER_OUT]. */
export const SHOULDER_IN = 4.9;
export const SHOULDER_OUT = 7.3;
const TOWER_X = 6.6;
const TOWER_H = 21;

export type PropKind = 'sandbags' | 'barrier' | 'hulk';
export interface PropPlacement {
  kind: PropKind;
  x: number; // centre, world X (signed)
  z: number; // centre, segment-local z
  halfX: number; // half extent across the bridge
}

/** Plan 20 P20.1: per-theme dressing opts. Missing tier => 'high'; missing theme => legacy output. */
export interface DressingOpts {
  theme?: EnvTheme;
  tier?: QualityTier;
}

/** Plan 20 bundled-cluster kinds (the per-theme vocabulary tables below pick from these). */
export type ClusterKind = 'crates' | 'spool' | 'sign' | 'girders' | 'container' | 'drums' | 'tyres' | 'nest';
export interface ClusterPlacement {
  kind: ClusterKind;
  x: number; // centre, world X (signed) — the outer shoulder band
  z: number; // centre, segment-local z
  slot: number; // ring-slot key the placement was hashed from (drives the in-bundle variation)
}

const PROP_HALF_X: Record<PropKind, number> = { sandbags: 0.7, barrier: 0.45, hulk: 0.95 };
const SLOT_Z = [4, 20, 36]; // clear of lamps (10, 30) and the truss at z=40

// Segments are built once per ring slot and recycled (world segment w sits in slot w mod ringSize), so a
// tower on slot 0 is the only pattern that stays evenly spaced for every ring size: one per ring length.
export const hasTower = (index: number): boolean => index === 0;

/** Deterministic war-prop layout for a segment, both shoulders. */
export function propPlacements(index: number): PropPlacement[] {
  const out: PropPlacement[] = [];
  let hulkUsed = false;
  for (const side of [-1, 1]) {
    SLOT_Z.forEach((z, s) => {
      if (z === 20 && hasTower(index)) return;
      const h = hashValue(index, s * 2 + (side > 0 ? 1 : 0), 0x6a4);
      if (h < 0.5) return;
      let kind: PropKind = h < 0.78 ? 'sandbags' : 'barrier';
      if (h > 0.93 && !hulkUsed) {
        kind = 'hulk';
        hulkUsed = true;
      }
      const halfX = PROP_HALF_X[kind];
      // Hug the rail side of the shoulder; the 0.15 jitter keeps repeats from lining up.
      const x = side * (SHOULDER_IN + 0.5 + halfX + hashValue(index, s, 0x77) * 0.15);
      out.push({ kind, x, z: z + (hashValue(index, s, 0x31) - 0.5) * 2, halfX });
    });
  }
  return out;
}

const WHITE = new Color3(1, 1, 1);

// Plan 20 P20.1 tints (vertex colours on the concrete material, like SAND/BURNT above).
const WOOD = new Color3(0.5, 0.38, 0.24);
const WOOD_LIGHT = new Color3(0.58, 0.46, 0.3);
const STEEL = new Color3(0.36, 0.38, 0.41);
const RUST = new Color3(0.42, 0.26, 0.16);
const TYRE = new Color3(0.13, 0.13, 0.14);
const STEEL_BLUE = new Color3(0.3, 0.36, 0.42);
const OLIVE = new Color3(0.36, 0.36, 0.27);
const CABLE = new Color3(0.16, 0.16, 0.17);
const SIGN_YELLOW = new Color3(0.72, 0.58, 0.16);
const SIGN_RED = new Color3(0.55, 0.2, 0.17);
const CHAR = new Color3(0.1, 0.09, 0.08); // dusk burnt-wood read
const CHAR_RED = new Color3(0.13, 0.09, 0.08); // dusk burnt-steel read (a touch warmer)
const NEST_SAND = new Color3(0.5, 0.43, 0.32); // darker than L1 SAND — soot through the weave
const BRAZIER_METAL = new Color3(0.2, 0.17, 0.15);
const CHARCOAL = new Color3(0.07, 0.06, 0.05); // the ember system (lane C) supplies the glow
const PATCH = new Color3(0.42, 0.43, 0.45);

/**
 * Plan 20 P20.1: per-theme cluster vocabulary. 'overcast' KEEPS today's war detritus (its set is
 * "today's PLUS") and interleaves its clusters around it; 'industrial'/'dusk' replace the detritus
 * with their own sets — dusk picks from everything, built with the burnt tints, plus its dedicated
 * nest + brazier pass (braziers come from brazierPlacements, not this table).
 */
const THEME_CLUSTERS: Record<EnvTheme, readonly ClusterKind[]> = {
  overcast: ['crates', 'spool', 'sign', 'girders'],
  industrial: ['container', 'drums', 'tyres'],
  dusk: ['crates', 'spool', 'sign', 'girders', 'container', 'drums', 'tyres', 'nest'],
};

// Overcast interleaves around today's SLOT_Z detritus (4/20/36, whose barrier tails reach ~24.1);
// industrial/dusk own the whole ladder. Both stay clear of the lamp posts (z 10/30) and, on slot-0
// segments, of the tower legs (z ~19.5–20.5 — no slot lands there).
const CLUSTER_SLOTS: Record<EnvTheme, readonly number[]> = {
  overcast: [14, 27],
  industrial: [4, 14, 26, 36],
  dusk: [4, 14, 26, 36],
};

const SEED_OCCUPY = 0x5b1;
const SEED_KIND = 0x3e9;
const SEED_JITTER = 0x2ad;
const SEED_TRIM = 0x70f; // the Low density halve

/**
 * Plan 20 P20.1: bundled prop-cluster layout for a themed segment — pure in (index, theme, tier),
 * keyed by ring slot like propPlacements. Low drops ~half the clusters via a build-time hash-range
 * cut (deterministic: same slot ⇒ same props on every rebuild; braziers are NOT subject to it).
 */
export function themeClusters(index: number, theme: EnvTheme, tier: QualityTier = 'high'): ClusterPlacement[] {
  const kinds = THEME_CLUSTERS[theme];
  const out: ClusterPlacement[] = [];
  for (const side of [-1, 1]) {
    CLUSTER_SLOTS[theme].forEach((z, s) => {
      const slot = s * 2 + (side > 0 ? 1 : 0);
      if (hashValue(index, slot, SEED_OCCUPY) < 0.35) return;
      if (tier === 'low' && hashValue(index, slot, SEED_TRIM) < 0.5) return;
      out.push({
        kind: kinds[Math.floor(hashValue(index, slot, SEED_KIND) * kinds.length)]!,
        x: side * (6.0 + hashValue(index, slot + 8, SEED_JITTER) * 0.3),
        z: z + (hashValue(index, slot + 16, SEED_JITTER) - 0.5) * 1.2,
        slot,
      });
    });
  }
  return out;
}

export interface BrazierPlacement {
  x: number; // centre, world X (signed) — outer shoulder, clear of the parapet's inner face
  z: number; // centre, segment-local z
  topY: number; // top of the charcoal load — where lane C's ember system emits
}

/**
 * Plan 20 P20.1: the L3 'dusk' brazier slots — one per side per segment (the lamp-post rhythm),
 * deterministic in the ring slot index and NOT subject to the Low density cut, so this export is
 * the single source of truth for every tier (lane C maps slot z to world z via the recycle state).
 * The geometry is a cold metal bowl on legs — no emissive mesh, no fire: the glow is lane C's
 * ember system (merged vertex colours clamp at 1.0; a second material would break the 0-draw rule).
 */
export function brazierPlacements(index: number): BrazierPlacement[] {
  const out: BrazierPlacement[] = [];
  for (const side of [-1, 1]) {
    const slot = side > 0 ? 1 : 0;
    out.push({
      x: side * (6.35 + hashValue(index, slot + 24, SEED_JITTER) * 0.18),
      z: (side < 0 ? 8 : 32) + (hashValue(index, slot + 32, SEED_JITTER) - 0.5) * 0.8,
      topY: 1.05, // charcoal tops at y 1.05 (see the builder: legs 0.62, bowl 0.6–0.92, charcoal ~0.89–1.05)
    });
  }
  return out;
}

/** Tint a part with one vertex colour (every concrete part needs the attribute for the merge). */
export function tint(mesh: Mesh, c: Color3 = WHITE): Mesh {
  const n = mesh.getTotalVertices();
  const col = new Array<number>(n * 4);
  for (let i = 0; i < n; i++) {
    col[i * 4] = c.r;
    col[i * 4 + 1] = c.g;
    col[i * 4 + 2] = c.b;
    col[i * 4 + 3] = 1;
  }
  mesh.setVerticesData(VertexBuffer.ColorKind, col);
  return mesh;
}

const PAINT = new Color3(1.15, 1.15, 1.1); // light grey against the dark asphalt
const DARK = new Color3(0.2, 0.2, 0.21);
const BURNT = new Color3(0.14, 0.12, 0.11);
const SAND = new Color3(0.62, 0.55, 0.42);
const BARRIER = new Color3(0.75, 0.75, 0.73);

/** Concrete-material parts for the segment (tinted). The caller merges them with the base parts. */
export function buildDressing(scene: Scene, index: number, opts?: DressingOpts): Mesh[] {
  const parts: Mesh[] = [];
  const box = (name: string, w: number, h: number, d: number, x: number, y: number, z: number, c: Color3 = WHITE, rotX = 0): Mesh => {
    const m = MeshBuilder.CreateBox(`${name}-${index}`, { width: w, height: h, depth: d }, scene);
    m.position.set(x, y, z);
    m.rotation.x = rotX;
    parts.push(tint(m, c));
    return m;
  };
  const cyl = (name: string, dia: number, h: number, x: number, y: number, z: number, c: Color3, tess = 6): void => {
    const m = MeshBuilder.CreateCylinder(`${name}-${index}`, { diameter: dia, height: h, tessellation: tess }, scene);
    m.position.set(x, y, z);
    parts.push(tint(m, c));
  };

  // Outer walkway + parapet either side (the bridge reads wider and gives the props somewhere to stand).
  const shW = SHOULDER_OUT - SHOULDER_IN;
  for (const side of [-1, 1]) {
    box('shoulder', shW, 1.1, SEG, side * (SHOULDER_IN + shW / 2), -0.55, SEG / 2);
    box('parapet', 0.25, 0.9, SEG, side * (SHOULDER_OUT - 0.125), 0.45, SEG / 2);
  }

  // Lane paint: solid edge lines + dashed lane dividers, 3 cm proud of the deck.
  for (const side of [-1, 1]) {
    box('edge', 0.12, 0.03, SEG, side * (LANE_HALF - 0.45), 0.015, SEG / 2, PAINT);
    for (let d = 0; d < 5; d++) box('dash', 0.1, 0.03, 4, side * 1.5, 0.015, 4 + d * 8, PAINT);
  }
  box('joint', LANE_HALF * 2, 0.03, 0.14, 0, 0.015, 0.1, PAINT); // expansion joint at the segment start

  // Lamp posts on the shoulder, every 20 m, leaning their head over the rail.
  for (const side of [-1, 1]) {
    for (const z of [10, 30]) {
      const x = side * 5.5;
      cyl('lamp', 0.16, 5.2, x, 2.6, z, DARK);
      parts[parts.length - 1]!.metadata = { surface: 'metal' };
      box('lamparm', 1.0, 0.1, 0.1, x - side * 0.5, 5.15, z, DARK).metadata = { surface: 'metal' };
      box('lamphead', 0.45, 0.14, 0.22, x - side, 5.08, z, DARK).metadata = { surface: 'metal' };
    }
  }

  // Suspension tower every 3rd segment: two legs, portal beams, and the main cables sagging to the deck.
  if (hasTower(index)) {
    const tz = SEG / 2;
    for (const side of [-1, 1]) {
      box('towerleg', 0.9, TOWER_H, 0.9, side * TOWER_X, TOWER_H / 2, tz);
      box('towerfoot', 1.3, 0.8, 1.3, side * TOWER_X, 0.4, tz);
    }
    box('towerbeam', TOWER_X * 2 + 0.9, 0.7, 0.7, 0, TOWER_H - 0.5, tz);
    box('towerbeam', TOWER_X * 2 + 0.9, 0.5, 0.5, 0, TOWER_H - 6, tz);
    // Catenary: 8 straight links from the tower top (y 20.4) down to the shoulder rail at 20 m either way.
    const steps = 8;
    const yTop = TOWER_H - 0.6;
    const yEnd = 1.1;
    const sag = (u: number): number => yEnd + (yTop - yEnd) * (1 - u) * (1 - u); // u 0..1 along the run
    for (const side of [-1, 1]) {
      for (const dir of [-1, 1]) {
        for (let k = 0; k < steps; k++) {
          const u0 = k / steps;
          const u1 = (k + 1) / steps;
          const y0 = sag(u0);
          const y1 = sag(u1);
          const dz = (u1 - u0) * (SEG / 2);
          const len = Math.hypot(dz, y1 - y0);
          box('cable', 0.12, 0.12, len, side * TOWER_X, (y0 + y1) / 2, tz + dir * (u0 + u1) * (SEG / 4), DARK, Math.atan2(y0 - y1, dir * dz));
          if (k % 2 === 0) box('hanger', 0.05, y0 - 0.3, 0.05, side * TOWER_X, (y0 + 0.3) / 2, tz + dir * u0 * (SEG / 2), DARK);
        }
      }
    }
  }

  // War props, outside the lanes (see propPlacements). Plan 20: 'overcast' keeps them (its set is
  // "today's PLUS"); 'industrial'/'dusk' replace the detritus with their own tables — and with NO
  // theme this loop runs exactly as before (the golden parity test pins that).
  if (opts?.theme === undefined || opts.theme === 'overcast') {
    for (const p of propPlacements(index)) {
    if (p.kind === 'sandbags') {
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3 - r; c++) {
          box('sandbag', 0.5, 0.28, 0.34, p.x + (c - (2 - r) / 2) * 0.5, 0.14 + r * 0.28, p.z + (r % 2) * 0.05, SAND);
        }
      }
    } else if (p.kind === 'barrier') {
      box('barrier', 0.9, 0.8, 2.6, p.x, 0.4, p.z, BARRIER);
      box('barrier', 0.5, 0.5, 2.6, p.x, 0.25, p.z + 2.8, BARRIER);
    } else {
      // Burnt car hulk: body, cab, four wheels.
      box('hulk', 1.8, 0.65, 4.0, p.x, 0.62, p.z, BURNT).metadata = { surface: 'metal' };
      box('hulkcab', 1.5, 0.55, 2.0, p.x, 1.2, p.z - 0.2, BURNT).metadata = { surface: 'metal' };
      for (const wx of [-0.85, 0.85]) {
        for (const wz of [-1.3, 1.3]) {
          const m = MeshBuilder.CreateCylinder(`wheel-${index}`, { diameter: 0.62, height: 0.25, tessellation: 8 }, scene);
          m.rotation.z = Math.PI / 2;
          m.position.set(p.x + wx, 0.31, p.z + wz);
          m.metadata = { surface: 'prop' };
          parts.push(tint(m, DARK));
        }
      }
    }
    }
  }

  // Road repairs and bridge maintenance vary by ring slot and remain flush with the deck.
  if (opts?.theme !== undefined) {
    const variant = index % 4;
    if (variant !== 0) {
      const x = variant === 1 ? -1.5 : variant === 2 ? 1.5 : 0;
      box('road-repair', variant === 3 ? 3.4 : 2.4, 0.02, 5 + variant, x, 0.022, 18 + variant * 3, PATCH);
      box('repair-seam', variant === 3 ? 3.4 : 2.4, 0.025, 0.08, x, 0.035, 15.5 + variant * 2.5, DARK);
    }
    const side = index % 2 ? 1 : -1;
    if (variant === 1 || variant === 3) {
      box('service-platform', 0.9, 0.18, 3.2, side * 7.65, 0.05, 24, STEEL);
      box('damaged-rail', 0.12, 0.12, 2.8, side * 7.2, 0.65, 24, RUST, 0.18);
    } else if (variant === 2) {
      box('loose-cable', 0.1, 0.1, 5, side * 7.12, 0.95, 25, DARK, 0.14);
    }
  }

  // Plan 20 P20.1: per-theme vocabulary (bundled clusters + dusk braziers + tiny details).
  if (opts?.theme !== undefined) parts.push(...buildThemeDressing(scene, index, opts.theme, opts.tier ?? 'high'));
  return parts;
}

/** Theme-pass part factory — same contract as the legacy helpers: tinted, material-free, merged. */
function themeBox(scene: Scene, parts: Mesh[], index: number, name: string, w: number, h: number, d: number, x: number, y: number, z: number, c: Color3, rotY = 0, rotZ = 0, rotX = 0): Mesh {
  const m = MeshBuilder.CreateBox(`${name}-${index}`, { width: w, height: h, depth: d }, scene);
  m.position.set(x, y, z);
  m.rotation.set(rotX, rotY, rotZ);
  parts.push(tint(m, c));
  return m;
}

function themeCyl(scene: Scene, parts: Mesh[], index: number, name: string, dia: number, h: number, x: number, y: number, z: number, c: Color3, tess = 6, rotX = 0, diaTop?: number): Mesh {
  const m = MeshBuilder.CreateCylinder(`${name}-${index}`, { diameter: dia, height: h, tessellation: tess, diameterTop: diaTop }, scene);
  m.position.set(x, y, z);
  m.rotation.x = rotX;
  parts.push(tint(m, c));
  return m;
}

/**
 * Plan 20 P20.1 lane A: the per-theme dressing pass. Every bundle is big + small + tiny (never a
 * uniform row — the scenery-research placement rule); dusk builds the same vocabulary with burnt
 * tints plus sandbag nests and the brazier bases. New props live in the outer shoulder band
 * (centres |x| ∈ [5.95, 6.9], extents inside the parapet at 7.3) and stay ≤ 6 m above the deck —
 * the suspension towers (21 m) are legacy output and untouched. Low skips the tiny-details
 * category entirely and themeClusters has already dropped half the bundles.
 */
function buildThemeDressing(scene: Scene, index: number, theme: EnvTheme, tier: QualityTier): Mesh[] {
  const parts: Mesh[] = [];
  const box = themeBox.bind(null, scene, parts, index);
  const cyl = themeCyl.bind(null, scene, parts, index);
  const burnt = theme === 'dusk';

  for (const p of themeClusters(index, theme, tier)) {
    const start = parts.length;
    const v = (n: number): number => hashValue(index, p.slot * 64 + n, 0x4c2); // per-bundle variation stream
    switch (p.kind) {
      case 'crates': {
        const wood = burnt ? CHAR : v(1) < 0.5 ? WOOD : WOOD_LIGHT;
        const s = 0.85 + v(2) * 0.25;
        // 2x2 base stacked along the SEGMENT (x jitter stays tiny — the shoulder band is ~1.3 m wide).
        for (let r = 0; r < 2; r++) {
          for (let c = 0; c < 2; c++) {
            box('crate', s, s * 0.8, s, p.x + (v(3 + r * 2 + c) - 0.5) * 0.14, s * 0.4 + r * s * 0.8, p.z + (c - 0.5) * (s + 0.05) + (v(7 + r * 2 + c) - 0.5) * 0.2, wood, (v(9) - 0.5) * 0.3);
          }
        }
        box('crate', s * 0.72, s * 0.6, s * 0.72, p.x + (v(10) - 0.5) * 0.3, s * 1.9, p.z + (v(11) - 0.5) * 0.3, wood, (v(12) - 0.5) * 0.7);
        const dz = (v(13) < 0.5 ? -1 : 1) * (s + 0.5);
        box('crate', 0.55, 0.5, 0.55, p.x + (v(14) - 0.5) * 0.2, 0.25, p.z + dz, wood, v(15) * 0.9);
        box('crate', 0.3, 0.28, 0.3, p.x + (v(16) - 0.5) * 0.7, 0.14, p.z + dz + (v(17) < 0.5 ? -1 : 1) * 0.5, wood, v(18));
        break;
      }
      case 'spool': {
        const wood = burnt ? CHAR : WOOD;
        const cable = burnt ? CHAR_RED : CABLE;
        cyl('spool', 1.65, 0.16, p.x, 0.83, p.z - 0.55, wood, 8, Math.PI / 2);
        cyl('spool', 1.65, 0.16, p.x, 0.83, p.z + 0.55, wood, 8, Math.PI / 2);
        cyl('spool', 0.5, 1.26, p.x, 0.83, p.z, cable, 6, Math.PI / 2);
        cyl('spool', 1.0, 0.8, p.x, 0.83, p.z + (v(1) - 0.5) * 0.2, cable, 8, Math.PI / 2);
        const dz = v(3) < 0.5 ? -1 : 1;
        cyl('spool', 0.42, 0.3, p.x + (v(2) - 0.5) * 0.3, 0.21, p.z + dz * 1.5, cable, 6, Math.PI / 2);
        box('spool', 0.16, 0.16, 0.6, p.x + (v(4) - 0.5) * 0.6, 0.08, p.z - dz * 1.7, cable);
        break;
      }
      case 'sign': {
        const plate = burnt ? CHAR : v(1) < 0.45 ? SIGN_YELLOW : SIGN_RED;
        cyl('sign', 0.09, 2.6, p.x, 1.3, p.z, DARK, 6);
        box('sign', 0.95, 0.7, 0.05, p.x, 2.32, p.z, plate, (v(2) - 0.5) * 0.8);
        box('sign', 0.05, 0.55, 0.05, p.x, 1.9, p.z, DARK);
        const dz = v(3) < 0.5 ? -1 : 1;
        cyl('sign', 0.06, 1.15, p.x + 0.14, 0.575, p.z + dz * 1.3, DARK, 6);
        box('sign', 0.44, 0.3, 0.04, p.x + 0.14, 1.05, p.z + dz * 1.3, plate, v(4) - 0.5);
        box('sign', 0.11, 0.11, 0.03, p.x, 2.45, p.z + 0.045, plate);
        break;
      }
      case 'girders': {
        const steel = burnt ? CHAR_RED : STEEL;
        for (let g = 0; g < 3; g++) {
          const len = 2.9 + v(1 + g) * 1.0;
          box('girder', 0.26, 0.32, len, p.x + (g - 1) * 0.3 + (v(4 + g) - 0.5) * 0.14, 0.17 + g * 0.33, p.z + (v(7 + g) - 0.5) * 0.3, steel, (v(10 + g) - 0.5) * 0.28);
        }
        box('girder', 0.22, 0.26, 1.1, p.x + 0.28, 0.13, p.z + (v(13) < 0.5 ? -1 : 1) * 1.9, steel, v(14) - 0.5);
        box('girder', 0.4, 0.05, 0.5, p.x - 0.3, 0.03, p.z + (v(15) - 0.5) * 2.2, steel);
        break;
      }
      case 'container': {
        const tintC = burnt ? CHAR_RED : [STEEL_BLUE, RUST, OLIVE][Math.floor(v(1) * 3)]!;
        const out = Math.sign(p.x);
        box('container', 1.3, 2.45, 4.0, p.x, 1.225, p.z, tintC);
        for (let r = 0; r < 6; r++) box('container', 0.07, 2.15, 0.38, p.x - out * 0.66, 1.2, p.z - 1.65 + r * 0.66, tintC);
        box('container', 1.36, 0.28, 0.5, p.x, 2.35, p.z + 1.9, tintC);
        box('container', 1.22, 1.5, 1.8, p.x + out * 0.08, 0.75, p.z - 1.35, tintC, (v(2) - 0.5) * 0.15);
        box('container', 0.06, 1.3, 1.1, p.x + out * 0.7, 0.62, p.z + 1.1, tintC, 0, out * 0.28); // panel leaning on the outer face
        box('container', 0.2, 0.2, 0.2, p.x - out * 0.5, 0.1, p.z + 2.15, tintC);
        box('container', 0.16, 0.16, 0.3, p.x + out * 0.45, 0.08, p.z - 2.1, tintC);
        break;
      }
      case 'drums': {
        const tintD = burnt ? CHAR_RED : v(1) < 0.6 ? RUST : STEEL_BLUE;
        const off: [number, number, number][] = [[-0.35, -0.32, 0], [0.35, -0.3, 0], [0, 0.34, 0], [0, 0.01, 1]];
        for (const [ox, oz, up] of off) {
          cyl('drum', 0.58, 0.88, p.x + ox + (v(2) - 0.5) * 0.1, 0.44 + up * 0.88, p.z + oz + (v(3) - 0.5) * 0.2, tintD, 8);
        }
        const dz = v(4) < 0.5 ? -1 : 1;
        cyl('drum', 0.58, 0.88, p.x + 0.15, 0.29, p.z + dz * 1.35, tintD, 8, Math.PI / 2); // tipped
        cyl('drum', 0.5, 0.06, p.x - 0.2, 0.53, p.z - dz * 1.15, tintD, 6);
        cyl('drum', 0.5, 0.06, p.x - 0.2, 0.6, p.z - dz * 1.05, tintD, 6);
        break;
      }
      case 'tyres': {
        const ty = burnt ? CHAR : TYRE;
        const lays: [number, number, number][] = [[0, -0.6, 0], [0.06, 0, 0], [0, 0.6, 0], [0.02, -0.3, 0.21], [-0.02, 0.3, 0.21]];
        for (const [ox, oz, oy] of lays) cyl('tyre', 0.68, 0.21, p.x + ox, 0.105 + oy, p.z + oz + (v(1) - 0.5) * 0.2, ty, 6);
        cyl('tyre', 0.68, 0.21, p.x + 0.1, 0.34, p.z + (v(2) < 0.5 ? -1 : 1) * 1.25, ty, 6, Math.PI / 2 - 0.12); // standing, leaning
        box('tyre', 0.24, 0.1, 0.24, p.x - 0.3, 0.05, p.z + (v(3) - 0.5) * 1.6, ty);
        break;
      }
      case 'nest': {
        for (let r = 0; r < 2; r++) {
          const n = 5 - r;
          for (let b = 0; b < n; b++) {
            box('nest', 0.52, 0.3, 0.4, p.x + (r ? 0 : 0.12) + (v(1 + r * 5 + b) - 0.5) * 0.1, 0.15 + r * 0.3, p.z + (b - (n - 1) / 2) * 0.46 + r * 0.2, NEST_SAND, (v(11 + r * 5 + b) - 0.5) * 0.3);
          }
        }
        for (let b = 0; b < 3; b++) box('nest', 0.5, 0.28, 0.38, p.x + (v(21 + b) - 0.5) * 0.9, 0.14, p.z + (v(24 + b) - 0.5) * 3.4, NEST_SAND, v(27 + b));
        break;
      }
    }
    const surface = p.kind === 'crates' || p.kind === 'spool' || p.kind === 'tyres' ? 'prop' : p.kind === 'nest' ? 'concrete' : 'metal';
    for (const part of parts.slice(start)) part.metadata = { surface };
  }

  // Dusk brazier bases — always built (never thinned: the export is the ember system's contract).
  if (theme === 'dusk') {
    for (const b of brazierPlacements(index)) {
      for (let l = 0; l < 3; l++) {
        const a = (l / 3) * Math.PI * 2 + 0.5;
        box('brazier', 0.07, 0.62, 0.07, b.x + Math.cos(a) * 0.24, 0.31, b.z + Math.sin(a) * 0.24, BRAZIER_METAL);
      }
      cyl('brazier', 0.85, 0.32, b.x, 0.76, b.z, BRAZIER_METAL, 8, 0, 0.42); // the bowl (frustum)
      for (let c = 0; c < 4; c++) {
        const w = 0.16 + hashValue(index, c * 3 + 60, 0x59a) * 0.1;
        box('brazier', w, 0.16, w, b.x + (hashValue(index, c * 3 + 62, 0x59a) - 0.5) * 0.5, 0.97, b.z + (hashValue(index, c * 3 + 63, 0x59a) - 0.5) * 0.5, CHARCOAL, hashValue(index, c * 3 + 64, 0x59a) * 0.8);
      }
    }
  }

  // Tiny-details pass — deck bolts, parapet rail clamps, deck patches (Low skips the category).
  if (tier !== 'low') {
    for (const side of [-1, 1]) {
      const s0 = side > 0 ? 50 : 0;
      for (let i = 0; i < 7; i++) {
        box('bolt', 0.11, 0.05, 0.11, side * (6.0 + hashValue(index, s0 + i, 0xb01) * 1.1), 0.026, 3 + i * 5.2 + (hashValue(index, s0 + i, 0xb02) - 0.5) * 1.6, DARK);
      }
      for (let i = 0; i < 5; i++) {
        box('clamp', 0.2, 0.13, 0.17, side * 7.14, 0.86, 6.5 + i * 7.2 + (hashValue(index, s0 + i, 0xc01) - 0.5) * 2.2, STEEL);
      }
      for (let i = 0; i < 3; i++) {
        box('patch', 0.6 + hashValue(index, s0 + i, 0xd01) * 0.4, 0.035, 0.8 + hashValue(index, s0 + i, 0xd02) * 0.7, side * (5.95 + hashValue(index, s0 + i, 0xd03) * 0.65), 0.02, 5 + i * 12.5 + (hashValue(index, s0 + i, 0xd04) - 0.5) * 3.5, PATCH, (hashValue(index, s0 + i, 0xd05) - 0.5) * 0.4);
      }
    }
  }
  return parts;
}
